/**
 * Expanded Careers Database v2.0
 * Includes military-friendly careers, trades, apprenticeships, and certification pathways
 */

import { ExpandedCareer } from '../types/expandedCareerTypes';

export const EXPANDED_CAREERS_V2: ExpandedCareer[] = [
  // ===================================
  // MILITARY & GOVERNMENT
  // ===================================
  {
    id: 'border_patrol_agent',
    title: 'Border Patrol Agent',
    category: 'Law Enforcement',
    tier: 'mid',
    education_required: {
      minimum: 'hs_diploma',
      alternatives: ['Military experience', 'Law enforcement experience']
    },
    certifications_required: [],
    certifications_preferred: [
      {
        id: 'spanish_proficiency',
        name: 'Spanish Language Proficiency',
        provider: 'Various',
        cost: 0,
        time_weeks: 52,
        renewal_required: false,
        online_available: true,
        gi_bill_eligible: false
      }
    ],
    experience_required: {
      years_minimum: 0,
      years_preferred: 2,
      relevant_roles: ['military', 'law_enforcement', 'security']
    },
    requiredSkills: ['law_enforcement', 'physical_fitness', 'Spanish', 'decision_making'],
    niceToHave: ['military_experience', 'firearms_proficiency', 'first_aid'],
    keywords: ['law_enforcement', 'government', 'security', 'patrol', 'immigration'],
    salary: {
      entry_min: 52000,
      entry_max: 62000,
      mid_min: 70000,
      mid_max: 95000
    },
    market_outlook: {
      growth_rate: 5,
      annual_openings: 1500,
      competition_level: 'high'
    },
    work_environment: {
      remote_available: false,
      hybrid_available: false,
      physical_demands: ['outdoor_work', 'physically_demanding', 'irregular_hours'],
      work_conditions: ['outdoor', 'all_weather', 'remote_locations'],
      typical_schedule: 'Rotating shifts including nights, weekends, holidays'
    },
    common_entry_points: ['military', 'law_enforcement', 'security_guard'],
    common_next_steps: ['supervisory_border_patrol_agent', 'customs_officer'],
    training_paths: [
      {
        id: 'cbp_academy',
        name: 'CBP Border Patrol Academy',
        type: 'certification',
        provider: 'U.S. Customs and Border Protection',
        cost: 0, // Government pays
        time_months: 5,
        prerequisites: ['US_citizenship', 'background_check', 'physical_fitness'],
        outcomes: {
          completion_rate: 85,
          job_placement_rate: 100,
          avg_salary_increase: 52000
        },
        impact: 'critical',
        gi_bill_eligible: false // Federal training program
      }
    ],
    veteran_preference: true,
    military_mos_relevant: ['11B', '0311', '31B', '1811'],
    description: 'Patrol borders to prevent illegal entry, smuggling, and trafficking. Requires physical fitness, decision-making under pressure, and often Spanish language skills.',
    dailyActivities: [
      'Patrol assigned border areas on foot, vehicle, or horseback',
      'Detect and apprehend individuals crossing illegally',
      'Process and transport detainees',
      'Maintain surveillance equipment and vehicles'
    ],
    commonTransitionsFrom: ['military_infantry', 'security_guard', 'police_officer'],
    is_active: true,
    last_updated: new Date()
  },

  // ===================================
  // HEALTHCARE - MILITARY TRANSITION
  // ===================================
  {
    id: 'emergency_medical_technician',
    title: 'Emergency Medical Technician (EMT)',
    category: 'Healthcare',
    tier: 'entry',
    education_required: {
      minimum: 'hs_diploma',
      alternatives: ['Military medical training (68W, HM)']
    },
    certifications_required: [
      {
        id: 'emt_basic',
        name: 'EMT-Basic Certification',
        provider: 'National Registry of Emergency Medical Technicians',
        cost: 1500,
        time_weeks: 12,
        renewal_required: true,
        renewal_period_years: 2,
        online_available: false,
        pass_rate: 70,
        gi_bill_eligible: true
      }
    ],
    certifications_preferred: [
      {
        id: 'cpr_bls',
        name: 'CPR/BLS for Healthcare Providers',
        provider: 'American Heart Association',
        cost: 80,
        time_weeks: 1,
        renewal_required: true,
        renewal_period_years: 2,
        online_available: true,
        gi_bill_eligible: false
      }
    ],
    experience_required: {
      years_minimum: 0,
      years_preferred: 0,
      relevant_roles: []
    },
    requiredSkills: ['emergency_medical_care', 'patient_assessment', 'CPR', 'vital_signs', 'communication'],
    niceToHave: ['medical_terminology', 'IV_therapy', 'Spanish'],
    keywords: ['healthcare', 'emergency', 'EMT', 'ambulance', 'first_responder'],
    salary: {
      entry_min: 30000,
      entry_max: 45000,
      mid_min: 40000,
      mid_max: 55000
    },
    market_outlook: {
      growth_rate: 11,
      annual_openings: 20000,
      competition_level: 'medium'
    },
    work_environment: {
      remote_available: false,
      hybrid_available: false,
      physical_demands: ['lifting_patients', 'physical_stamina', 'irregular_hours'],
      work_conditions: ['emergency_situations', 'stressful', 'all_weather'],
      typical_schedule: '12 or 24-hour shifts, including nights/weekends'
    },
    common_entry_points: ['military_medic', 'healthcare_support', 'caregiver'],
    common_next_steps: ['paramedic', 'firefighter_paramedic', 'emergency_room_technician'],
    prerequisite_roles: [],
    training_paths: [
      {
        id: 'emt_basic_program',
        name: 'EMT-Basic Training Program',
        type: 'certification',
        provider: 'Community colleges, fire departments',
        cost: 1500,
        time_months: 3,
        prerequisites: ['hs_diploma', 'background_check', 'physical_exam'],
        outcomes: {
          completion_rate: 85,
          job_placement_rate: 80,
          avg_salary_increase: 35000
        },
        impact: 'critical',
        gi_bill_eligible: true
      }
    ],
    veteran_preference: true,
    military_mos_relevant: ['68W', 'HM', '4N0X1'],
    description: 'Provide emergency medical care and transportation for sick and injured. Many veterans with medical training can fast-track certification.',
    dailyActivities: [
      'Respond to 911 emergency calls',
      'Assess patient condition and provide emergency care',
      'Transport patients to medical facilities',
      'Document patient care and maintain equipment'
    ],
    commonTransitionsFrom: ['military_medic', 'caregiver', 'healthcare_support'],
    is_active: true,
    last_updated: new Date()
  },

  {
    id: 'paramedic',
    title: 'Paramedic',
    category: 'Healthcare',
    tier: 'mid',
    education_required: {
      minimum: 'hs_diploma',
      preferred: 'associates',
      alternatives: ['EMT certification + paramedic program']
    },
    certifications_required: [
      {
        id: 'paramedic_certification',
        name: 'Paramedic Certification',
        provider: 'National Registry of Emergency Medical Technicians',
        cost: 8000,
        time_weeks: 52,
        renewal_required: true,
        renewal_period_years: 2,
        online_available: false,
        pass_rate: 65,
        gi_bill_eligible: true
      }
    ],
    certifications_preferred: [
      {
        id: 'acls',
        name: 'Advanced Cardiac Life Support (ACLS)',
        provider: 'American Heart Association',
        cost: 250,
        time_weeks: 2,
        renewal_required: true,
        renewal_period_years: 2,
        online_available: false,
        gi_bill_eligible: true
      }
    ],
    experience_required: {
      years_minimum: 1,
      years_preferred: 2,
      relevant_roles: ['emergency_medical_technician']
    },
    requiredSkills: ['advanced_emergency_care', 'IV_therapy', 'medication_administration', 'intubation', 'critical_thinking'],
    niceToHave: ['trauma_care', 'cardiac_care', 'pediatric_care'],
    keywords: ['healthcare', 'emergency', 'paramedic', 'advanced_life_support', 'EMS'],
    salary: {
      entry_min: 40000,
      entry_max: 60000,
      mid_min: 55000,
      mid_max: 75000,
      senior_min: 70000,
      senior_max: 90000
    },
    market_outlook: {
      growth_rate: 11,
      annual_openings: 15000,
      competition_level: 'medium'
    },
    work_environment: {
      remote_available: false,
      hybrid_available: false,
      physical_demands: ['lifting_patients', 'physical_stamina', 'high_stress'],
      work_conditions: ['emergency_situations', 'critical_care', 'all_weather'],
      typical_schedule: '12 or 24-hour shifts'
    },
    common_entry_points: ['emergency_medical_technician'],
    common_next_steps: ['flight_paramedic', 'critical_care_paramedic', 'paramedic_supervisor'],
    prerequisite_roles: ['emergency_medical_technician'],
    training_paths: [
      {
        id: 'paramedic_program',
        name: 'Paramedic Training Program',
        type: 'certification',
        provider: 'Community colleges, technical schools',
        cost: 8000,
        time_months: 12,
        prerequisites: ['emt_certification', '1_year_experience'],
        outcomes: {
          completion_rate: 75,
          job_placement_rate: 90,
          avg_salary_increase: 20000
        },
        impact: 'critical',
        gi_bill_eligible: true
      }
    ],
    veteran_preference: true,
    military_mos_relevant: ['68W', 'HM', '4N0X1'],
    description: 'Provide advanced emergency medical care including medications, IV therapy, and advanced airway management. Natural progression from EMT.',
    dailyActivities: [
      'Respond to critical emergency calls',
      'Provide advanced life support interventions',
      'Administer medications and IV therapy',
      'Make critical decisions in life-threatening situations'
    ],
    commonTransitionsFrom: ['emergency_medical_technician', 'military_medic'],
    is_active: true,
    last_updated: new Date()
  },

  // ===================================
  // SKILLED TRADES
  // ===================================
  {
    id: 'hvac_technician',
    title: 'HVAC Technician',
    category: 'Trades',
    tier: 'mid',
    education_required: {
      minimum: 'hs_diploma',
      alternatives: ['Trade school', 'Apprenticeship completion']
    },
    certifications_required: [
      {
        id: 'epa_608',
        name: 'EPA Section 608 Certification',
        provider: 'Environmental Protection Agency',
        cost: 150,
        time_weeks: 2,
        renewal_required: false,
        online_available: true,
        pass_rate: 85,
        gi_bill_eligible: false
      }
    ],
    certifications_preferred: [
      {
        id: 'hvac_excellence',
        name: 'HVAC Excellence Certification',
        provider: 'HVAC Excellence',
        cost: 500,
        time_weeks: 24,
        renewal_required: true,
        renewal_period_years: 5,
        online_available: false,
        gi_bill_eligible: true
      }
    ],
    experience_required: {
      years_minimum: 0,
      years_preferred: 2,
      relevant_roles: ['hvac_apprentice', 'maintenance_technician']
    },
    requiredSkills: ['HVAC_systems', 'electrical', 'refrigeration', 'troubleshooting', 'customer_service'],
    niceToHave: ['welding', 'sheet_metal', 'building_automation'],
    keywords: ['trades', 'HVAC', 'mechanical', 'refrigeration', 'heating', 'cooling'],
    salary: {
      entry_min: 40000,
      entry_max: 55000,
      mid_min: 55000,
      mid_max: 75000,
      senior_min: 75000,
      senior_max: 110000
    },
    market_outlook: {
      growth_rate: 13,
      annual_openings: 38000,
      competition_level: 'low'
    },
    work_environment: {
      remote_available: false,
      hybrid_available: false,
      physical_demands: ['lifting_equipment', 'climbing', 'confined_spaces', 'outdoor_work'],
      work_conditions: ['attics', 'basements', 'rooftops', 'all_weather'],
      typical_schedule: 'Full-time, on-call rotation, overtime common'
    },
    common_entry_points: ['hvac_apprentice', 'maintenance_worker', 'military_facilities'],
    common_next_steps: ['senior_hvac_tech', 'hvac_shop_manager', 'hvac_business_owner'],
    prerequisite_roles: [],
    training_paths: [
      {
        id: 'hvac_apprenticeship',
        name: 'HVAC Apprenticeship Program',
        type: 'apprenticeship',
        provider: 'Local unions, trade schools',
        cost: 2000,
        time_months: 24,
        prerequisites: ['hs_diploma', 'drivers_license', 'basic_math'],
        outcomes: {
          completion_rate: 70,
          job_placement_rate: 95,
          avg_salary_increase: 45000
        },
        impact: 'critical',
        gi_bill_eligible: true,
        earn_while_learning: true,
        starting_wage: 18
      }
    ],
    apprenticeship_available: true,
    union_affiliation: 'UA (United Association) or SMACNA',
    licensing_required: {
      state_specific: true,
      license_name: 'State HVAC License',
      exam_required: true
    },
    description: 'Install, maintain, and repair heating, ventilation, air conditioning, and refrigeration systems. High demand, excellent pay, apprenticeships available.',
    dailyActivities: [
      'Install and repair HVAC systems in homes and businesses',
      'Diagnose system problems and recommend solutions',
      'Perform preventive maintenance',
      'Interact with customers and explain technical issues'
    ],
    commonTransitionsFrom: ['maintenance_worker', 'military_facilities', 'construction'],
    is_active: true,
    last_updated: new Date()
  },

  {
    id: 'electrician',
    title: 'Electrician',
    category: 'Trades',
    tier: 'mid',
    education_required: {
      minimum: 'hs_diploma',
      alternatives: ['Apprenticeship completion', 'Trade school + experience']
    },
    certifications_required: [],
    certifications_preferred: [
      {
        id: 'journeyman_electrician',
        name: 'Journeyman Electrician License',
        provider: 'State licensing board',
        cost: 500,
        time_weeks: 208, // 4 years apprenticeship
        renewal_required: true,
        renewal_period_years: 3,
        online_available: false,
        gi_bill_eligible: true
      }
    ],
    experience_required: {
      years_minimum: 0,
      years_preferred: 4,
      relevant_roles: ['electrical_apprentice', 'maintenance_electrician']
    },
    requiredSkills: ['electrical_systems', 'wiring', 'blueprints', 'NEC_code', 'troubleshooting', 'safety'],
    niceToHave: ['PLC', 'industrial_controls', 'solar_installation'],
    keywords: ['trades', 'electrical', 'wiring', 'electrician', 'construction'],
    salary: {
      entry_min: 35000,
      entry_max: 50000,
      mid_min: 55000,
      mid_max: 75000,
      senior_min: 75000,
      senior_max: 100000
    },
    market_outlook: {
      growth_rate: 9,
      annual_openings: 80000,
      competition_level: 'low'
    },
    work_environment: {
      remote_available: false,
      hybrid_available: false,
      physical_demands: ['climbing_ladders', 'confined_spaces', 'lifting', 'standing'],
      work_conditions: ['construction_sites', 'indoor_outdoor', 'variable_conditions'],
      typical_schedule: 'Full-time, overtime common, on-call rotation'
    },
    common_entry_points: ['electrical_apprentice', 'military_electrician', 'construction_laborer'],
    common_next_steps: ['master_electrician', 'electrical_contractor', 'electrical_inspector'],
    prerequisite_roles: [],
    training_paths: [
      {
        id: 'ibew_apprenticeship',
        name: 'IBEW Electrical Apprenticeship',
        type: 'union_program',
        provider: 'International Brotherhood of Electrical Workers',
        cost: 1000, // Books and tools
        time_months: 48,
        prerequisites: ['hs_diploma', 'algebra', 'aptitude_test'],
        outcomes: {
          completion_rate: 75,
          job_placement_rate: 98,
          avg_salary_increase: 60000
        },
        impact: 'critical',
        gi_bill_eligible: true,
        earn_while_learning: true,
        starting_wage: 20
      }
    ],
    apprenticeship_available: true,
    union_affiliation: 'IBEW (International Brotherhood of Electrical Workers)',
    licensing_required: {
      state_specific: true,
      license_name: 'Journeyman/Master Electrician License',
      exam_required: true
    },
    military_mos_relevant: ['12R', 'CE', '3E0X1'],
    description: 'Install, maintain, and repair electrical systems in homes, businesses, and industrial facilities. Union apprenticeships offer earn-while-you-learn opportunities.',
    dailyActivities: [
      'Install electrical wiring and systems',
      'Read and interpret blueprints and technical diagrams',
      'Troubleshoot electrical problems',
      'Ensure all work meets electrical code requirements'
    ],
    commonTransitionsFrom: ['electrical_apprentice', 'military_electrician', 'construction'],
    is_active: true,
    last_updated: new Date()
  },

  // ===================================
  // IT/CYBERSECURITY - MILITARY TRANSITION
  // ===================================
  {
    id: 'cybersecurity_analyst',
    title: 'Cybersecurity Analyst',
    category: 'Information Technology',
    tier: 'mid',
    education_required: {
      minimum: 'hs_diploma',
      preferred: 'associates',
      alternatives: ['Military IT/Cyber experience + certifications', 'Bootcamp + certifications']
    },
    certifications_required: [
      {
        id: 'security_plus',
        name: 'CompTIA Security+',
        provider: 'CompTIA',
        cost: 400,
        time_weeks: 8,
        renewal_required: true,
        renewal_period_years: 3,
        online_available: true,
        pass_rate: 75,
        gi_bill_eligible: true
      }
    ],
    certifications_preferred: [
      {
        id: 'cissp',
        name: 'Certified Information Systems Security Professional',
        provider: 'ISC2',
        cost: 1200,
        time_weeks: 24,
        renewal_required: true,
        renewal_period_years: 3,
        online_available: true,
        gi_bill_eligible: true
      }
    ],
    experience_required: {
      years_minimum: 1,
      years_preferred: 3,
      relevant_roles: ['it_support_specialist', 'network_technician', 'military_it']
    },
    requiredSkills: ['cybersecurity', 'network_security', 'threat_detection', 'incident_response', 'security_tools'],
    niceToHave: ['penetration_testing', 'SIEM', 'Python', 'cloud_security'],
    keywords: ['cybersecurity', 'IT', 'security', 'SOC', 'threat_detection'],
    salary: {
      entry_min: 55000,
      entry_max: 75000,
      mid_min: 75000,
      mid_max: 105000,
      senior_min: 105000,
      senior_max: 150000
    },
    market_outlook: {
      growth_rate: 35,
      annual_openings: 55000,
      competition_level: 'medium'
    },
    work_environment: {
      remote_available: true,
      hybrid_available: true,
      typical_schedule: 'Full-time, may include on-call rotation'
    },
    common_entry_points: ['it_help_desk', 'network_technician', 'military_cyber'],
    common_next_steps: ['senior_cybersecurity_analyst', 'security_engineer', 'penetration_tester'],
    prerequisite_roles: [],
    training_paths: [
      {
        id: 'cybersecurity_bootcamp',
        name: 'Cybersecurity Analyst Bootcamp',
        type: 'bootcamp',
        provider: 'Various bootcamp providers',
        cost: 12000,
        time_months: 6,
        prerequisites: ['basic_IT_knowledge'],
        outcomes: {
          completion_rate: 80,
          job_placement_rate: 75,
          avg_salary_increase: 30000
        },
        impact: 'high',
        gi_bill_eligible: true
      }
    ],
    veteran_preference: true,
    security_clearance_preferred: 'secret',
    military_mos_relevant: ['25D', '3D0X2', 'CTN', '1B4X1'],
    description: 'Monitor networks for security breaches, investigate incidents, and implement security measures. High demand, especially for veterans with clearances.',
    dailyActivities: [
      'Monitor security systems for threats and breaches',
      'Investigate and respond to security incidents',
      'Analyze security logs and alerts',
      'Implement and maintain security tools'
    ],
    commonTransitionsFrom: ['military_cyber', 'it_support', 'network_administrator'],
    is_active: true,
    last_updated: new Date()
  },

  // ===================================
  // PUBLIC SAFETY & FIRST RESPONDERS
  // ===================================
  {
    id: 'police_officer',
    title: 'Police Officer',
    category: 'Public Safety',
    tier: 'mid',
    education_required: {
      minimum: 'hs_diploma',
      preferred: 'associates',
      alternatives: ['Military service + police academy']
    },
    certifications_required: [
      {
        id: 'police_academy',
        name: 'Police Academy Certification',
        provider: 'State police academy',
        cost: 5000,
        time_weeks: 24,
        renewal_required: false,
        online_available: false,
        pass_rate: 85,
        gi_bill_eligible: true
      }
    ],
    certifications_preferred: [],
    experience_required: {
      years_minimum: 0,
      years_preferred: 2,
      relevant_roles: ['military', 'security', 'corrections']
    },
    requiredSkills: ['law_enforcement', 'physical_fitness', 'decision_making', 'communication', 'conflict_resolution'],
    niceToHave: ['military_experience', 'crisis_intervention', 'bilingual'],
    keywords: ['police', 'law_enforcement', 'public_safety', 'patrol', 'criminal_justice'],
    salary: {
      entry_min: 42000,
      entry_max: 58000,
      mid_min: 60000,
      mid_max: 80000,
      senior_min: 80000,
      senior_max: 110000
    },
    market_outlook: {
      growth_rate: 3,
      annual_openings: 50000,
      competition_level: 'high'
    },
    work_environment: {
      remote_available: false,
      hybrid_available: false,
      physical_demands: ['physical_fitness', 'irregular_hours', 'high_stress'],
      work_conditions: ['outdoor', 'vehicle_patrol', 'emergency_response'],
      typical_schedule: 'Rotating shifts including nights, weekends, holidays'
    },
    common_entry_points: ['military', 'security_guard', 'corrections_officer'],
    common_next_steps: ['detective', 'sergeant', 'k9_officer', 'SWAT'],
    training_paths: [
      {
        id: 'police_academy_program',
        name: 'Police Academy Training',
        type: 'certification',
        provider: 'State police academy',
        cost: 5000,
        time_months: 6,
        prerequisites: ['background_check', 'physical_fitness', 'psychological_eval'],
        outcomes: {
          completion_rate: 85,
          job_placement_rate: 90,
          avg_salary_increase: 45000
        },
        impact: 'critical',
        gi_bill_eligible: true
      }
    ],
    veteran_preference: true,
    military_mos_relevant: ['11B', '0311', '31B', '1811', 'MA'],
    description: 'Protect communities by enforcing laws, responding to emergencies, and maintaining public safety. Strong veteran preference with 10-point hiring advantage.',
    dailyActivities: [
      'Patrol assigned areas and respond to calls',
      'Investigate crimes and gather evidence',
      'Make arrests and testify in court',
      'Provide community assistance and build relationships'
    ],
    commonTransitionsFrom: ['military_mp', 'security', 'corrections'],
    is_active: true,
    last_updated: new Date()
  },

  {
    id: 'firefighter_emt',
    title: 'Firefighter/EMT',
    category: 'Public Safety',
    tier: 'mid',
    education_required: {
      minimum: 'hs_diploma',
      alternatives: ['Military fire service experience']
    },
    certifications_required: [
      {
        id: 'emt_basic',
        name: 'EMT-Basic Certification',
        provider: 'NREMT',
        cost: 1500,
        time_weeks: 12,
        renewal_required: true,
        renewal_period_years: 2,
        online_available: false,
        gi_bill_eligible: true
      },
      {
        id: 'firefighter_1',
        name: 'Firefighter I Certification',
        provider: 'State fire marshal',
        cost: 3000,
        time_weeks: 16,
        renewal_required: false,
        online_available: false,
        gi_bill_eligible: true
      }
    ],
    certifications_preferred: [
      {
        id: 'paramedic',
        name: 'Paramedic Certification',
        provider: 'NREMT',
        cost: 8000,
        time_weeks: 52,
        renewal_required: true,
        renewal_period_years: 2,
        online_available: false,
        gi_bill_eligible: true
      }
    ],
    experience_required: {
      years_minimum: 0,
      years_preferred: 1,
      relevant_roles: ['emt', 'military_firefighter']
    },
    requiredSkills: ['emergency_response', 'physical_fitness', 'teamwork', 'fire_suppression', 'emergency_medical'],
    niceToHave: ['hazmat', 'technical_rescue', 'paramedic'],
    keywords: ['firefighter', 'emt', 'public_safety', 'emergency', 'rescue'],
    salary: {
      entry_min: 45000,
      entry_max: 62000,
      mid_min: 60000,
      mid_max: 85000,
      senior_min: 80000,
      senior_max: 120000
    },
    market_outlook: {
      growth_rate: 6,
      annual_openings: 27000,
      competition_level: 'very_high'
    },
    work_environment: {
      remote_available: false,
      hybrid_available: false,
      physical_demands: ['extremely_physically_demanding', 'hazardous_conditions'],
      work_conditions: ['fire_scenes', 'emergency_medical', 'rescue_operations'],
      typical_schedule: '24-hour shifts with 48 hours off'
    },
    common_entry_points: ['emt', 'military_firefighter', 'volunteer_firefighter'],
    common_next_steps: ['paramedic_firefighter', 'fire_captain', 'fire_inspector'],
    training_paths: [
      {
        id: 'fire_academy',
        name: 'Fire Academy Training',
        type: 'certification',
        provider: 'Local fire department or academy',
        cost: 4500,
        time_months: 4,
        prerequisites: ['emt_cert', 'physical_fitness', 'background_check'],
        outcomes: {
          completion_rate: 80,
          job_placement_rate: 70,
          avg_salary_increase: 50000
        },
        impact: 'critical',
        gi_bill_eligible: true
      }
    ],
    veteran_preference: true,
    military_mos_relevant: ['68W', 'HM', 'Firefighter'],
    description: 'Respond to fires, medical emergencies, and rescues. Requires EMT certification and firefighter training. Highly competitive with excellent benefits.',
    dailyActivities: [
      'Respond to fire alarms and medical emergencies',
      'Perform fire suppression and rescue operations',
      'Provide emergency medical care',
      'Maintain equipment and conduct training drills'
    ],
    commonTransitionsFrom: ['emt', 'military_firefighter', 'volunteer_fire'],
    is_active: true,
    last_updated: new Date()
  },

  {
    id: 'corrections_officer',
    title: 'Corrections Officer',
    category: 'Public Safety',
    tier: 'entry',
    education_required: {
      minimum: 'hs_diploma',
      alternatives: ['Military experience']
    },
    certifications_required: [],
    certifications_preferred: [],
    experience_required: {
      years_minimum: 0,
      years_preferred: 0,
      relevant_roles: ['military', 'security', 'law_enforcement']
    },
    requiredSkills: ['conflict_resolution', 'observation', 'physical_fitness', 'communication', 'crisis_management'],
    niceToHave: ['military_experience', 'self_defense', 'counseling'],
    keywords: ['corrections', 'prison', 'jail', 'security', 'criminal_justice'],
    salary: {
      entry_min: 38000,
      entry_max: 52000,
      mid_min: 50000,
      mid_max: 68000
    },
    market_outlook: {
      growth_rate: -5,
      annual_openings: 20000,
      competition_level: 'low'
    },
    work_environment: {
      remote_available: false,
      hybrid_available: false,
      physical_demands: ['standing', 'physical_confrontations'],
      work_conditions: ['prison_facility', 'high_stress', 'potentially_dangerous'],
      typical_schedule: 'Rotating shifts including nights, weekends, holidays'
    },
    common_entry_points: ['military', 'security_guard', 'entry_level'],
    common_next_steps: ['senior_corrections_officer', 'corrections_sergeant', 'probation_officer'],
    training_paths: [
      {
        id: 'corrections_academy',
        name: 'Corrections Officer Academy',
        type: 'certification',
        provider: 'Department of Corrections',
        cost: 0, // Employer pays
        time_months: 2,
        prerequisites: ['background_check', 'drug_test', 'psychological_eval'],
        outcomes: {
          completion_rate: 90,
          job_placement_rate: 95,
          avg_salary_increase: 40000
        },
        impact: 'critical',
        gi_bill_eligible: false // Employer-provided training
      }
    ],
    veteran_preference: true,
    military_mos_relevant: ['31B', '31E', 'MA', '5811'],
    description: 'Supervise individuals in jails and prisons, maintain security, and ensure safety. Good entry point with veteran preference and on-the-job training.',
    dailyActivities: [
      'Monitor inmate activities and conduct counts',
      'Search cells and common areas for contraband',
      'Respond to emergencies and disturbances',
      'Process inmates and maintain records'
    ],
    commonTransitionsFrom: ['military', 'security', 'entry_level'],
    is_active: true,
    last_updated: new Date()
  },

  {
    id: 'emergency_dispatcher',
    title: '911 Emergency Dispatcher',
    category: 'Public Safety',
    tier: 'entry',
    education_required: {
      minimum: 'hs_diploma'
    },
    certifications_required: [
      {
        id: 'emergency_dispatcher_cert',
        name: 'Emergency Dispatcher Certification',
        provider: 'APCO or NENA',
        cost: 800,
        time_weeks: 8,
        renewal_required: true,
        renewal_period_years: 2,
        online_available: true,
        gi_bill_eligible: true
      }
    ],
    certifications_preferred: [],
    experience_required: {
      years_minimum: 0,
      years_preferred: 0,
      relevant_roles: []
    },
    requiredSkills: ['multitasking', 'communication', 'typing', 'stress_management', 'active_listening'],
    niceToHave: ['bilingual', 'customer_service', 'technical'],
    keywords: ['dispatcher', '911', 'emergency', 'communications', 'public_safety'],
    salary: {
      entry_min: 35000,
      entry_max: 48000,
      mid_min: 45000,
      mid_max: 62000
    },
    market_outlook: {
      growth_rate: 4,
      annual_openings: 10000,
      competition_level: 'medium'
    },
    work_environment: {
      remote_available: false,
      hybrid_available: false,
      work_conditions: ['call_center', 'high_stress', 'emotionally_demanding'],
      typical_schedule: '12-hour shifts, rotating days/nights/weekends'
    },
    common_entry_points: ['call_center', 'customer_service', 'entry_level'],
    common_next_steps: ['supervisor', 'training_coordinator', 'communications_manager'],
    training_paths: [
      {
        id: 'dispatcher_training',
        name: '911 Dispatcher Training Program',
        type: 'certification',
        provider: 'Local emergency services',
        cost: 800,
        time_months: 2,
        prerequisites: ['background_check', 'typing_test'],
        outcomes: {
          completion_rate: 85,
          job_placement_rate: 80,
          avg_salary_increase: 38000
        },
        impact: 'critical',
        gi_bill_eligible: true
      }
    ],
    description: 'Answer 911 calls, dispatch emergency services, and provide life-saving instructions. Critical role requiring calm under pressure.',
    dailyActivities: [
      'Answer emergency calls and assess situations',
      'Dispatch police, fire, or medical services',
      'Provide pre-arrival instructions and support',
      'Maintain communication with first responders'
    ],
    commonTransitionsFrom: ['call_center', 'customer_service', 'administrative'],
    is_active: true,
    last_updated: new Date()
  },

  // ===================================
  // TRANSPORTATION & LOGISTICS
  // ===================================
  {
    id: 'cdl_truck_driver',
    title: 'Commercial Truck Driver (CDL)',
    category: 'Transportation',
    tier: 'entry',
    education_required: {
      minimum: 'hs_diploma'
    },
    certifications_required: [
      {
        id: 'cdl_class_a',
        name: 'Commercial Driver License (Class A)',
        provider: 'State DMV',
        cost: 3500,
        time_weeks: 6,
        renewal_required: true,
        renewal_period_years: 5,
        online_available: false,
        pass_rate: 80,
        gi_bill_eligible: true
      }
    ],
    certifications_preferred: [
      {
        id: 'hazmat_endorsement',
        name: 'HAZMAT Endorsement',
        provider: 'TSA',
        cost: 200,
        time_weeks: 2,
        renewal_required: true,
        renewal_period_years: 5,
        online_available: false,
        gi_bill_eligible: false
      }
    ],
    experience_required: {
      years_minimum: 0,
      years_preferred: 0,
      relevant_roles: []
    },
    requiredSkills: ['driving', 'navigation', 'time_management', 'safety_compliance'],
    niceToHave: ['mechanical_knowledge', 'hazmat', 'tanker_experience'],
    keywords: ['truck_driver', 'CDL', 'transportation', 'logistics', 'commercial_driving'],
    salary: {
      entry_min: 45000,
      entry_max: 60000,
      mid_min: 55000,
      mid_max: 75000,
      senior_min: 70000,
      senior_max: 90000
    },
    market_outlook: {
      growth_rate: 6,
      annual_openings: 230000,
      competition_level: 'low'
    },
    work_environment: {
      remote_available: false,
      hybrid_available: false,
      physical_demands: ['long_hours_sitting', 'loading_unloading'],
      work_conditions: ['truck_cab', 'away_from_home', 'irregular_hours'],
      typical_schedule: 'Varies: local daily, regional 2-3 nights, OTR weeks at a time'
    },
    common_entry_points: ['88M_military', 'delivery_driver', 'warehouse'],
    common_next_steps: ['owner_operator', 'driver_trainer', 'logistics_coordinator'],
    training_paths: [
      {
        id: 'cdl_training',
        name: 'CDL Class A Training Program',
        type: 'certification',
        provider: 'CDL schools, community colleges',
        cost: 3500,
        time_months: 2,
        prerequisites: ['valid_drivers_license', 'clean_driving_record'],
        outcomes: {
          completion_rate: 85,
          job_placement_rate: 95,
          avg_salary_increase: 50000
        },
        impact: 'critical',
        gi_bill_eligible: true
      }
    ],
    military_mos_relevant: ['88M'],
    description: 'Transport goods across local, regional, or national routes. High demand with many companies offering paid CDL training.',
    dailyActivities: [
      'Drive commercial vehicles safely and efficiently',
      'Plan routes and manage delivery schedules',
      'Load and secure cargo',
      'Perform pre-trip and post-trip vehicle inspections'
    ],
    commonTransitionsFrom: ['military_truck_driver', 'delivery_driver', 'warehouse'],
    is_active: true,
    last_updated: new Date()
  },

  // ===================================
  // HEALTHCARE CERTIFICATIONS
  // ===================================
  {
    id: 'medical_coder',
    title: 'Medical Coder',
    category: 'Healthcare',
    tier: 'entry',
    education_required: {
      minimum: 'hs_diploma',
      alternatives: ['Medical coding certification']
    },
    certifications_required: [
      {
        id: 'cpc',
        name: 'Certified Professional Coder (CPC)',
        provider: 'AAPC',
        cost: 1800,
        time_weeks: 16,
        renewal_required: true,
        renewal_period_years: 2,
        online_available: true,
        pass_rate: 70,
        gi_bill_eligible: true
      }
    ],
    certifications_preferred: [],
    experience_required: {
      years_minimum: 0,
      years_preferred: 1,
      relevant_roles: ['medical_billing', 'healthcare_admin']
    },
    requiredSkills: ['medical_terminology', 'attention_to_detail', 'ICD10', 'CPT_codes', 'anatomy'],
    niceToHave: ['healthcare_experience', 'billing_software', 'HIPAA_knowledge'],
    keywords: ['medical_coding', 'healthcare', 'billing', 'insurance', 'remote'],
    salary: {
      entry_min: 38000,
      entry_max: 52000,
      mid_min: 50000,
      mid_max: 68000
    },
    market_outlook: {
      growth_rate: 9,
      annual_openings: 30000,
      competition_level: 'medium'
    },
    work_environment: {
      remote_available: true,
      hybrid_available: true,
      typical_schedule: 'Full-time, business hours, many remote positions'
    },
    common_entry_points: ['medical_billing', 'healthcare_admin', 'data_entry'],
    common_next_steps: ['senior_coder', 'coding_auditor', 'revenue_cycle_manager'],
    training_paths: [
      {
        id: 'cpc_training',
        name: 'Medical Coding Certification Program',
        type: 'online_course',
        provider: 'AAPC, AHIMA',
        cost: 1800,
        time_months: 4,
        prerequisites: ['medical_terminology'],
        outcomes: {
          completion_rate: 75,
          job_placement_rate: 80,
          avg_salary_increase: 42000
        },
        impact: 'critical',
        gi_bill_eligible: true
      }
    ],
    description: 'Translate medical services into standardized codes for billing and insurance. High demand with many remote opportunities.',
    dailyActivities: [
      'Review medical records and assign diagnostic codes',
      'Ensure coding accuracy and compliance',
      'Work with healthcare providers on documentation',
      'Stay current with coding updates and regulations'
    ],
    commonTransitionsFrom: ['medical_billing', 'healthcare_admin', 'data_entry'],
    is_active: true,
    last_updated: new Date()
  },

  {
    id: 'phlebotomist',
    title: 'Phlebotomist',
    category: 'Healthcare',
    tier: 'entry',
    education_required: {
      minimum: 'hs_diploma'
    },
    certifications_required: [
      {
        id: 'phlebotomy_cert',
        name: 'Phlebotomy Technician Certification',
        provider: 'ASCP or NPA',
        cost: 1200,
        time_weeks: 8,
        renewal_required: true,
        renewal_period_years: 2,
        online_available: false,
        pass_rate: 85,
        gi_bill_eligible: true
      }
    ],
    certifications_preferred: [],
    experience_required: {
      years_minimum: 0,
      years_preferred: 0,
      relevant_roles: []
    },
    requiredSkills: ['venipuncture', 'patient_interaction', 'attention_to_detail', 'infection_control'],
    niceToHave: ['medical_terminology', 'customer_service', 'bilingual'],
    keywords: ['phlebotomy', 'healthcare', 'lab', 'blood_draw', 'medical'],
    salary: {
      entry_min: 30000,
      entry_max: 42000,
      mid_min: 38000,
      mid_max: 50000
    },
    market_outlook: {
      growth_rate: 10,
      annual_openings: 25000,
      competition_level: 'low'
    },
    work_environment: {
      remote_available: false,
      hybrid_available: false,
      typical_schedule: 'Varies: hospitals 24/7, clinics business hours'
    },
    common_entry_points: ['healthcare_support', 'customer_service', 'entry_level'],
    common_next_steps: ['lab_technician', 'phlebotomy_supervisor', 'medical_assistant'],
    training_paths: [
      {
        id: 'phlebotomy_training',
        name: 'Phlebotomy Training Program',
        type: 'certification',
        provider: 'Community colleges, technical schools',
        cost: 1200,
        time_months: 2,
        prerequisites: ['hs_diploma', 'background_check'],
        outcomes: {
          completion_rate: 90,
          job_placement_rate: 85,
          avg_salary_increase: 33000
        },
        impact: 'critical',
        gi_bill_eligible: true
      }
    ],
    description: 'Draw blood samples for testing, transfusions, and donations. Quick training with consistent demand in hospitals and clinics.',
    dailyActivities: [
      'Collect blood samples from patients',
      'Label and process specimens',
      'Verify patient information and maintain records',
      'Ensure patient comfort and safety'
    ],
    commonTransitionsFrom: ['customer_service', 'healthcare_support', 'entry_level'],
    is_active: true,
    last_updated: new Date()
  },

  // ===================================
  // MORE SKILLED TRADES
  // ===================================
  {
    id: 'welder',
    title: 'Welder',
    category: 'Trades',
    tier: 'entry',
    education_required: {
      minimum: 'hs_diploma',
      alternatives: ['Welding certification', 'Apprenticeship']
    },
    certifications_required: [
      {
        id: 'aws_certified_welder',
        name: 'AWS Certified Welder',
        provider: 'American Welding Society',
        cost: 800,
        time_weeks: 12,
        renewal_required: true,
        renewal_period_years: 3,
        online_available: false,
        pass_rate: 75,
        gi_bill_eligible: true
      }
    ],
    certifications_preferred: [],
    experience_required: {
      years_minimum: 0,
      years_preferred: 1,
      relevant_roles: ['welding_helper', 'manufacturing']
    },
    requiredSkills: ['welding', 'blueprint_reading', 'metal_fabrication', 'safety', 'precision'],
    niceToHave: ['TIG_welding', 'pipe_welding', 'underwater_welding', 'inspection'],
    keywords: ['welder', 'welding', 'trades', 'fabrication', 'manufacturing'],
    salary: {
      entry_min: 38000,
      entry_max: 52000,
      mid_min: 50000,
      mid_max: 70000,
      senior_min: 65000,
      senior_max: 95000
    },
    market_outlook: {
      growth_rate: 2,
      annual_openings: 49000,
      competition_level: 'low'
    },
    work_environment: {
      remote_available: false,
      hybrid_available: false,
      physical_demands: ['standing', 'physical_stamina', 'dexterity'],
      work_conditions: ['manufacturing', 'construction', 'shipyards', 'hot_conditions'],
      typical_schedule: 'Full-time, overtime common, some travel for projects'
    },
    common_entry_points: ['manufacturing', 'construction_laborer', 'military_welder'],
    common_next_steps: ['certified_welding_inspector', 'welding_supervisor', 'specialized_welder'],
    training_paths: [
      {
        id: 'welding_program',
        name: 'Welding Technology Program',
        type: 'certification',
        provider: 'Technical schools, community colleges',
        cost: 5000,
        time_months: 6,
        prerequisites: ['hs_diploma'],
        outcomes: {
          completion_rate: 80,
          job_placement_rate: 90,
          avg_salary_increase: 42000
        },
        impact: 'critical',
        gi_bill_eligible: true
      }
    ],
    military_mos_relevant: ['12W', '21C', 'HT'],
    description: 'Join metal parts using various welding techniques. Specialized welders (underwater, pipe, aerospace) can earn $80K+.',
    dailyActivities: [
      'Read blueprints and specifications',
      'Set up welding equipment and materials',
      'Weld metal components using various techniques',
      'Inspect welds for quality and defects'
    ],
    commonTransitionsFrom: ['manufacturing', 'construction', 'military_welder'],
    is_active: true,
    last_updated: new Date()
  },

  {
    id: 'heavy_equipment_operator',
    title: 'Heavy Equipment Operator',
    category: 'Trades',
    tier: 'entry',
    education_required: {
      minimum: 'hs_diploma',
      alternatives: ['Heavy equipment training', 'Military equipment operator']
    },
    certifications_required: [
      {
        id: 'heavy_equipment_cert',
        name: 'Heavy Equipment Operator Certification',
        provider: 'NCCCO or similar',
        cost: 2000,
        time_weeks: 8,
        renewal_required: true,
        renewal_period_years: 5,
        online_available: false,
        gi_bill_eligible: true
      }
    ],
    certifications_preferred: [],
    experience_required: {
      years_minimum: 0,
      years_preferred: 1,
      relevant_roles: ['military_equipment_operator', 'construction_laborer']
    },
    requiredSkills: ['equipment_operation', 'spatial_awareness', 'safety', 'maintenance_basics'],
    niceToHave: ['crane_operator', 'specialized_equipment', 'CDL'],
    keywords: ['heavy_equipment', 'operator', 'construction', 'excavator', 'crane'],
    salary: {
      entry_min: 42000,
      entry_max: 58000,
      mid_min: 55000,
      mid_max: 75000,
      senior_min: 70000,
      senior_max: 95000
    },
    market_outlook: {
      growth_rate: 4,
      annual_openings: 40000,
      competition_level: 'low'
    },
    work_environment: {
      remote_available: false,
      hybrid_available: false,
      physical_demands: ['sitting_extended_periods', 'concentration'],
      work_conditions: ['construction_sites', 'outdoor', 'noise', 'weather'],
      typical_schedule: 'Full-time, overtime common, seasonal in some regions'
    },
    common_entry_points: ['military_equipment_operator', 'construction_laborer', 'warehouse'],
    common_next_steps: ['crane_operator', 'equipment_supervisor', 'construction_foreman'],
    training_paths: [
      {
        id: 'equipment_operator_training',
        name: 'Heavy Equipment Operator Training',
        type: 'certification',
        provider: 'Technical schools, union programs',
        cost: 2000,
        time_months: 2,
        prerequisites: ['valid_drivers_license'],
        outcomes: {
          completion_rate: 85,
          job_placement_rate: 90,
          avg_salary_increase: 48000
        },
        impact: 'critical',
        gi_bill_eligible: true
      }
    ],
    military_mos_relevant: ['12N', '12K', 'EO', '1345'],
    description: 'Operate bulldozers, excavators, cranes, and other heavy machinery for construction and industrial projects. High demand with good pay.',
    dailyActivities: [
      'Operate heavy equipment safely and efficiently',
      'Excavate, grade, and move earth and materials',
      'Perform equipment inspections and basic maintenance',
      'Follow project specifications and safety protocols'
    ],
    commonTransitionsFrom: ['military_equipment_op', 'construction', 'warehouse'],
    is_active: true,
    last_updated: new Date()
  },

  // ===================================
  // GOVERNMENT & FEDERAL
  // ===================================
  {
    id: 'tsa_officer',
    title: 'TSA Transportation Security Officer',
    category: 'Government',
    tier: 'entry',
    education_required: {
      minimum: 'hs_diploma'
    },
    certifications_required: [],
    certifications_preferred: [],
    experience_required: {
      years_minimum: 0,
      years_preferred: 0,
      relevant_roles: []
    },
    requiredSkills: ['security_screening', 'attention_to_detail', 'customer_service', 'communication'],
    niceToHave: ['military_experience', 'law_enforcement', 'bilingual'],
    keywords: ['TSA', 'airport_security', 'federal', 'government', 'homeland_security'],
    salary: {
      entry_min: 38000,
      entry_max: 48000,
      mid_min: 45000,
      mid_max: 62000
    },
    market_outlook: {
      growth_rate: 3,
      annual_openings: 8000,
      competition_level: 'medium'
    },
    work_environment: {
      remote_available: false,
      hybrid_available: false,
      work_conditions: ['airport', 'standing', 'repetitive_tasks'],
      typical_schedule: 'Rotating shifts including early mornings, nights, weekends, holidays'
    },
    common_entry_points: ['customer_service', 'security', 'military'],
    common_next_steps: ['lead_tso', 'supervisor', 'federal_air_marshal'],
    training_paths: [
      {
        id: 'tsa_training',
        name: 'TSA Training Program',
        type: 'certification',
        provider: 'Transportation Security Administration',
        cost: 0, // Federal training
        time_months: 1,
        prerequisites: ['US_citizenship', 'background_check', 'drug_test'],
        outcomes: {
          completion_rate: 85,
          job_placement_rate: 100,
          avg_salary_increase: 40000
        },
        impact: 'critical',
        gi_bill_eligible: false // Federal employer training
      }
    ],
    veteran_preference: true,
    description: 'Screen passengers and baggage at airports to ensure transportation security. Federal job with good benefits and veteran preference.',
    dailyActivities: [
      'Screen passengers and carry-on baggage',
      'Operate X-ray machines and detection equipment',
      'Enforce security regulations and procedures',
      'Provide customer service to travelers'
    ],
    commonTransitionsFrom: ['customer_service', 'security', 'military'],
    is_active: true,
    last_updated: new Date()
  },

  {
    id: 'postal_worker',
    title: 'Postal Service Mail Carrier',
    category: 'Government',
    tier: 'entry',
    education_required: {
      minimum: 'hs_diploma'
    },
    certifications_required: [],
    certifications_preferred: [],
    experience_required: {
      years_minimum: 0,
      years_preferred: 0,
      relevant_roles: []
    },
    requiredSkills: ['physical_stamina', 'organization', 'customer_service', 'navigation', 'driving'],
    niceToHave: ['military_experience', 'route_knowledge', 'weather_tolerance'],
    keywords: ['postal', 'mail_carrier', 'USPS', 'government', 'federal'],
    salary: {
      entry_min: 40000,
      entry_max: 52000,
      mid_min: 50000,
      mid_max: 65000
    },
    market_outlook: {
      growth_rate: -13,
      annual_openings: 20000,
      competition_level: 'high'
    },
    work_environment: {
      remote_available: false,
      hybrid_available: false,
      physical_demands: ['walking', 'carrying', 'outdoor_work'],
      work_conditions: ['all_weather', 'outdoor', 'vehicle'],
      typical_schedule: 'Full-time, early start times, may include Saturdays'
    },
    common_entry_points: ['delivery_driver', 'customer_service', 'military'],
    common_next_steps: ['supervisor', 'postmaster', 'postal_inspector'],
    training_paths: [
      {
        id: 'usps_training',
        name: 'USPS Carrier Training',
        type: 'certification',
        provider: 'United States Postal Service',
        cost: 0, // Employer training
        time_months: 1,
        prerequisites: ['pass_postal_exam', 'background_check', 'drug_test'],
        outcomes: {
          completion_rate: 90,
          job_placement_rate: 100,
          avg_salary_increase: 42000
        },
        impact: 'critical',
        gi_bill_eligible: false
      }
    ],
    veteran_preference: true,
    description: 'Deliver mail and packages along assigned routes. Federal job with excellent benefits, pension, and veteran preference in hiring.',
    dailyActivities: [
      'Sort and organize mail for delivery route',
      'Deliver mail and packages to residences and businesses',
      'Collect outgoing mail and payments',
      'Provide customer service and maintain delivery records'
    ],
    commonTransitionsFrom: ['delivery_driver', 'warehouse', 'military'],
    is_active: true,
    last_updated: new Date()
  }
];

export default EXPANDED_CAREERS_V2;
